import Button from '@/components/UI/Button';
import { DropdownMenuDivider } from '@/components/UI/Dropdown';
import Select from '@/components/UI/Select';
import { _isNil, _notNil } from '@/littledash';
import { MappedDevice, TargetField } from '@/model/Device.model';
import { useDevices } from '@/utils/devices/useDevices';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import NoDataCard from '@/components/NoDataCard';
import { web as webRoute } from '@/support/route.ts';
import { RouteService } from '@/support/RouteService';
import { useHistory } from 'react-router-dom';
import { generateMeasurementOptions } from './DeviceConnections.utils';

const initialTargetFields = [
  { label: 'Follow focus', value: 'follow-focus' },
  { label: 'Search Bar', value: 'focus-search' },
  { label: 'Dosing Weight', value: 'dosing-weight' },
  { label: 'Assign ID', value: 'assign-id' },
];

export const DeviceConnections: FC = () => {
  const [targetFields, setTargetFields] = useState(initialTargetFields);
  const {
    mappedDevices,
    onChangeDeviceTarget,
    onChangeDeviceType,
    activePreset,
    deviceTypes,
    getDevices,
    refreshDevices,
  } = useDevices({});

  const dispatch = useDispatch();
  const history = useHistory();

  const closeModal = () => {
    dispatch({ type: 'CLOSE_MODAL' });
  };
  const dashboard = () => {
    closeModal();
    history.push(webRoute('dashboard'));
  };

  useEffect(() => {
    refreshDevices(false);
  }, []);

  useEffect(() => {
    if (activePreset) {
      const targetFields = [...initialTargetFields];
      const measurementOptions = generateMeasurementOptions(activePreset.calculations);
      targetFields.push(...measurementOptions);
      setTargetFields(targetFields);
    }
  }, [activePreset]);

  return (
    <ModalContainer size="medium">
      <ModalHeader
        title="Device connections"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText="Find more information about connecting devices here. "
        readMoreLink={RouteService.help('device_connections')}
      />
      {_notNil(activePreset) ? (
        <div className="pa3">
          <div className="pb3 flex justify-between items-end">
            <div className="flex items-center">
              <label>Active preset: {activePreset.title}</label>
            </div>
            <div className="flex items-center">
              <Button
                onClick={() => {
                  getDevices();
                }}
                plain
                className="mr2"
              >
                Connect device
              </Button>
              <Button
                onClick={() => {
                  refreshDevices(true);
                }}
                plain
                icon="refresh"
                viewBox="0 0 24 24"
              />
            </div>
          </div>
          <DropdownMenuDivider />
          {_notNil(activePreset) && (
            <div>
              {mappedDevices?.map((mappedDevice: MappedDevice) => {
                const usbVendorId = mappedDevice.usb_vendor_id;
                const usbProductId = mappedDevice.usb_product_id;

                return (
                  <div key={usbProductId} className="pv3 flex justify-between">
                    <div className="pa1">
                      <div className="pb1 black-90">
                        {usbProductId || '-'} · {usbVendorId || '-'}
                      </div>
                      <div>{mappedDevice.name ?? 'Unmapped'}</div>
                    </div>
                    <div className="flex pa1 w-two-thirds">
                      <select
                        className="mw5 mb0 mr2"
                        value={mappedDevice.device_type_id}
                        onChange={(event) => {
                          if (_notNil(onChangeDeviceType) && _notNil(event?.target.value)) {
                            onChangeDeviceType(mappedDevice, event?.target.value);
                          }
                        }}
                      >
                        <option>Unselected</option>
                        {deviceTypes
                          .filter(
                            ({ usb_product_id, usb_vendor_id }) =>
                              usb_product_id === usbProductId && usb_vendor_id === usbVendorId
                          )
                          .map(({ title, id }) => (
                            <option key={id} value={id}>
                              {title}
                            </option>
                          ))}
                      </select>

                      <Select
                        value={mappedDevice?.target}
                        options={targetFields}
                        isMulti={true}
                        disabled={_isNil(mappedDevice?.device_type_id)}
                        onChange={(targets) => {
                          if (_notNil(onChangeDeviceTarget)) {
                            onChangeDeviceTarget(mappedDevice, targets as TargetField[]);
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="pa3">
          <NoDataCard
            title="Please navigate to a study to connect and map devices"
            dark
            onLinkClick={dashboard}
            btnTxt="Select a study"
            fluidHeight
          />
        </div>
      )}
    </ModalContainer>
  );
};
